import { useState, useMemo } from 'react';
import { Row, Col, Card, Form, Spinner, Badge } from 'react-bootstrap';
import { useAlerts, useAlertStats, useAckAlert } from '../../features/alerts/useAlerts';
import { useAuthContext } from '../../providers/AuthProvider';
import AlertItem from '../../components/alerts/AlertItem';
import { MACHINES as MACHINE_NAMES } from '../../data/operatorConstants.js';

const MACHINES = MACHINE_NAMES.map(m => m.toLowerCase());

const RULE_LABEL = {
  MACHINE_OFFLINE: 'Machine not responding',
  BREAKDOWN: 'Breakdown',
  MACHINE_STOPPED: 'Stopped with work loaded',
  UNATTRIBUTED_PRODUCTION: 'Producing with no operator',
  QUEUE_EMPTY: 'Empty queue',
  TEMP_DEVIATION: 'Temperature off setpoint',
  TEMP_RUNAWAY: 'Suspected shorted relay',
  TEMP_SENSOR_FAULT: 'Temperature sensor fault',
  DIAMETER_DEVIATION: 'Cable off size',
  GAUGE_FAULT: 'Laser gauge fault',
  SPARK_FAULT_BURST: 'Spark tester firing repeatedly',
  SETPOINT_OFF_STANDARD: 'Set off the standard'
};

const fmtMin = m => {
  const t = Math.round(Number(m) || 0);
  if (t < 60) return `${t} min`;
  const h = Math.floor(t / 60);
  const r = t % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const KpiCard = ({ title, value, subtitle, color = 'primary' }) => (
  <Card className="h-100">
    <Card.Body className="py-3">
      <p className="text-700 fs-10 mb-1">{title}</p>
      <h3 className={`mb-0 text-${color}`}>{value}</h3>
      {subtitle ? <p className="text-700 fs-10 mb-0 mt-1">{subtitle}</p> : null}
    </Card.Body>
  </Card>
);

/**
 * Current alerts and what has been firing.
 *
 * The history matters as much as the live list: a rule that fires constantly is
 * either a real recurring problem or a badly chosen threshold, and the only way
 * to tell them apart is to look at how much time it accounts for.
 */
const FactoryAlerts = () => {
  const [machineId, setMachineId] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [authState] = useAuthContext();

  const filters = useMemo(
    () => ({
      status: showResolved ? 'ALL' : 'OPEN,ACKNOWLEDGED',
      ...(machineId ? { machineId } : {}),
      limit: 100
    }),
    [machineId, showResolved]
  );

  const { data, isLoading } = useAlerts(filters);
  const { data: statsData } = useAlertStats(30);
  const ack = useAckAlert();

  const alerts = data?.data || [];
  const counts = data?.counts || {};
  const stats = statsData?.data || [];

  const onAck = alert =>
    ack.mutate({
      id: alert._id,
      actor: authState?.user?.name || authState?.user?.username || null
    });

  return (
    <div className="mb-9 mt-0">
      <div className="mb-4">
        <h2 className="mb-1">Factory Alerts</h2>
        <p className="text-700 mb-0">
          Conditions that are true right now. They open and close by themselves —
          acknowledging one stops it nagging without pretending it is over.
        </p>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <KpiCard title="Critical" value={counts.CRITICAL || 0} color="danger" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Warnings" value={counts.WARNING || 0} color="warning" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard title="Info" value={counts.INFO || 0} color="info" />
        </Col>
        <Col xs={6} md={3}>
          <KpiCard
            title="Rules Firing"
            value={stats.length}
            subtitle="last 30 days"
            color="success"
          />
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="mb-1 fs-9 text-700">Machine</Form.Label>
              <Form.Select size="sm" value={machineId} onChange={e => setMachineId(e.target.value)}>
                <option value="">All machines</option>
                {MACHINES.map(m => (
                  <option key={m} value={m}>
                    {m.toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="show-resolved"
                label="Include resolved"
                checked={showResolved}
                onChange={e => setShowResolved(e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col xs={12} lg={7}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">
                {showResolved ? 'All alerts' : 'Open alerts'}
                <span className="text-700 fs-10 ms-2">{alerts.length}</span>
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              {isLoading ? (
                <div className="d-flex align-items-center justify-content-center py-6 gap-2">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span className="fs-9 text-700">Loading…</span>
                </div>
              ) : !alerts.length ? (
                <div className="text-center py-6 text-700 fs-9">
                  Nothing needs attention.
                </div>
              ) : (
                alerts.map((a, i) => (
                  <div key={a._id} className={i !== alerts.length - 1 ? 'border-bottom border-200' : ''}>
                    <AlertItem alert={a} onAck={a.status === 'RESOLVED' ? null : onAck} />
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={5}>
          <Card>
            <Card.Header className="py-2">
              <h6 className="mb-0">What has been firing — last 30 days</h6>
              <p className="mb-0 fs-10 text-700">
                A rule that fires constantly is either a real recurring problem or
                a badly chosen threshold. Time accounted for tells them apart.
              </p>
            </Card.Header>
            <Card.Body className="p-0">
              {!stats.length ? (
                <div className="text-center py-5 text-700 fs-9">No history yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm fs-9 mb-0">
                    <thead>
                      <tr>
                        <th className="text-800">Rule</th>
                        <th className="text-800">Machine</th>
                        <th className="text-800 text-end">Times</th>
                        <th className="text-800 text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.map((r, i) => (
                        <tr key={`${r.rule}-${r.machineId}-${i}`}>
                          <td>
                            {RULE_LABEL[r.rule] || r.rule}
                            {r.openNow ? (
                              <Badge bg="danger" className="fs-10 ms-1">
                                {r.openNow} open
                              </Badge>
                            ) : null}
                          </td>
                          <td className="text-uppercase text-700">{r.machineId || '—'}</td>
                          <td className="text-end">{r.count}</td>
                          <td className="text-end fw-semibold">{fmtMin(r.totalMin)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FactoryAlerts;
